import { useEffect, useState, type ReactNode } from "react";
import { AmberEyebrow } from "./AmberEyebrow";
import { cn } from "@/lib/cn";

/**
 * ImpactHero — restrained per the brand guide.
 *
 * Chalk surface, Abyss type. The big number is Poppins Bold, the
 * narrative below is Crimson Pro for editorial warmth. ONE thin Atlantic
 * rule under the eyebrow as the "intentional pop" of colour. No
 * gradients, no decorative motifs (chalk-boxes belong over photography
 * and Abyss surfaces only).
 */

interface ImpactHeroProps {
  eyebrow?: ReactNode;
  /** Headline metric. Animates from 0 to its final value on first paint. */
  amount: number;
  /** Currency symbol prefix (e.g. "£", "$"). */
  currency?: string;
  /** Sentence following the amount; use <Mark> for one-or-two-word emphasis. */
  narrative: ReactNode;
  /** Optional secondary line under the narrative (period, scope). */
  meta?: ReactNode;
  /** Right-aligned content (one primary CTA at most). */
  trailing?: ReactNode;
  className?: string;
}

const PREFERS_REDUCED_MOTION =
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function useCountUp(target: number, durationMs = 900) {
  const [value, setValue] = useState(PREFERS_REDUCED_MOTION ? target : 0);

  useEffect(() => {
    if (PREFERS_REDUCED_MOTION) {
      setValue(target);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);

  return value;
}

export function ImpactHero({
  eyebrow = "Year to date",
  amount,
  currency = "£",
  narrative,
  meta,
  trailing,
  className,
}: ImpactHeroProps) {
  const animated = useCountUp(amount);
  const formatted = animated.toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <section
      className={cn(
        "flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between",
        className,
      )}
    >
      <div className="min-w-0 max-w-3xl">
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="h-px w-6 shrink-0"
            style={{ background: "var(--brand-atlantic)" }}
          />
          <AmberEyebrow tone="atlantic">{eyebrow}</AmberEyebrow>
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="font-brand text-3xl font-medium text-fg sm:text-4xl">
            {currency}
          </span>
          <span className="font-brand text-5xl font-bold tracking-tight tabular text-fg sm:text-6xl">
            {formatted}
          </span>
        </div>
        <p
          className="mt-4 text-lg leading-relaxed text-fg sm:text-xl"
          style={{ fontFamily: "var(--font-editorial)" }}
        >
          {narrative}
        </p>
        {meta && (
          <p className="mt-3 text-xs text-fg-muted">{meta}</p>
        )}
      </div>
      {trailing && <div className="shrink-0">{trailing}</div>}
    </section>
  );
}

/**
 * Markup helper — wraps key words in Crimson Pro Medium Italic per the
 * brand "markup system". Use sparingly: 1–2 words per sentence.
 *
 * Default tone is Atlantic (link-flavoured), Amber for a positive payoff
 * sentiment word. No tone = inherits the surrounding colour (just italic).
 */
export function Mark({
  children,
  tone = "atlantic",
}: {
  children: ReactNode;
  tone?: "default" | "atlantic" | "amber";
}) {
  const colour =
    tone === "amber"
      ? "var(--brand-amber)"
      : tone === "atlantic"
      ? "var(--brand-atlantic)"
      : undefined;
  return (
    <em
      style={{
        fontFamily: "var(--font-editorial)",
        fontStyle: "italic",
        fontWeight: 500,
        color: colour,
      }}
    >
      {children}
    </em>
  );
}
